import { useEffect, useMemo, useRef, useState, FC } from "react";

import { Skeleton, Stack, Typography } from "@mui/material";

import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { MdFullscreen as FullScreenIcon } from "react-icons/md";
import { useLocation, useParams, useSearchParams } from "react-router-dom";

import FullScreenInit from "components/FullScreenInit";
import { UnityViewer } from "components/UnityViewer";
import VirtualTourHeader from "components/VirtualTourHeader";

import { UnityViewerContextProvider } from "contexts/UnityViewerContext";

import { useGetOutputQuery } from "api/output/queries/useGetOutputQuery";
import { useGetVirtualTourAnnotationsQuery } from "api/virtual-tour/queries/useGetVirtualTourAnnotationsQuery";
import { useGetVirtualTourQuery } from "api/virtual-tour/queries/useGetVirtualTourQuery";
import { useGetVirtualTourReferenceMeasurements } from "api/virtual-tour/queries/useGetVirtualTourReferenceMeasurements";
import { isUnityViewerFullScreenAtom } from "store/atoms/isUnityViewerFullScreen";
import { newAnnotationAtom } from "store/atoms/newAnnotation";
import { vtourAnnotationsLengthAtom } from "store/atoms/vtourAnnotationsLengthAtom";

import { DisplayAnnotation } from "./DisplayAnnotation";
import ExistingReferenceMeasurements from "./ExistingReferenceMeasurements";
import { NewAnnotationDialog } from "./NewAnnotationDialog";
import { useLoading } from "contexts/LoadingContext";
import Toolbar from "components/ViewerWrapper/Toolbar";
import { Bounce, toast } from "react-toastify";
import ViewerWrapperLoader from "components/ViewerWrapper/ViewerWrapperLoader";
import BlurryOverlay from "components/BlurryOverlay";

type SearchParams = {
  annotationId: string;
  hotspotId: string;
  viewMode: "app" | "zen";
};

const VirtualTourViewer = ({
  mode = "uniscan",
}: {
  mode: "uniscan" | "biscan" | "uniscan-compare";
}) => {
  const setFullscreen = useSetAtom(isUnityViewerFullScreenAtom);
  const [newAnnotation, setNewAnnotation] = useAtom(newAnnotationAtom);
  const [searchParams, setSearchParams] = useSearchParams();
  const { projectSlug = "" } = useParams();
  const location = useLocation();
  const isPublic = useMemo(() => {
    return location.pathname.startsWith("/public");
  }, [location.pathname]);

  const { vtourOutputId1, fullscreen, blurryEffect } = useLoading();
  const selectedAnnotationId = searchParams.get("annotationId");
  const viewMode = searchParams.get("view-mode") || "app";
  const spanFullScreen = viewMode === "zen";
  const {
    data: output,
    isLoading: outputLoading,
    error: outputError,
  } = useGetOutputQuery(vtourOutputId1);
  const {
    data: annotations,
    isLoading: annotationsLoading,
    error: annotationsError,
  } = useGetVirtualTourAnnotationsQuery(output?.virtualTour?.id || "");
  const {
    // data: virtualTour,
    isLoading: virtualTourLoading,
    error: virtualTourError,
  } = useGetVirtualTourQuery(output?.virtualTour?.id || "");
  const { data: referenceMeasurements } =
    useGetVirtualTourReferenceMeasurements(output?.virtualTour?.id || "");
  const selectedAnnotation = useMemo(
    () => annotations?.find(({ id }) => selectedAnnotationId === id) || null,
    [annotations, selectedAnnotationId]
  );

  const projectName = useMemo(() => {
    const projectname = projectSlug
      .at(0)
      ?.toUpperCase()
      .concat(projectSlug.split("_").join(" ").substring(1));
    return projectname;
  }, [projectSlug]);

  // handle full screen state to be in sync with actual full screen event
  // useEffect(() => {
  //   const handleFullscreenChange = () => {
  //     if (document.fullscreenElement === ref.current) {
  //       console.debug("Fullscreen changed: true");
  //       setFullscreen(true);
  //     } else {
  //       console.debug("Fullscreen changed: false");
  //       setFullscreen(false);
  //     }
  //   };

  //   document.addEventListener("fullscreenchange", handleFullscreenChange);

  //   return () => {
  //     document.removeEventListener("fullscreenchange", handleFullscreenChange);
  //   };
  // }, [setFullscreen, ref]);

  // useEffect(() => {
  //   const handleFullscreenChange = () => {
  //     if (document.fullscreenElement === ref.current) {
  //       console.debug("Fullscreen changed: true");
  //       setFullscreen(true);
  //     } else {
  //       console.debug("Fullscreen changed: false");
  //       setFullscreen(false);
  //     }
  //   };

  //   document.addEventListener("fullscreenchange", handleFullscreenChange);

  //   return () => {
  //     document.removeEventListener("fullscreenchange", handleFullscreenChange);
  //   };
  // }, [setFullscreen, ref]);

  if (annotationsError || outputError || virtualTourError) {
    return toast.error(
      outputError?.message ||
        annotationsError?.message ||
        virtualTourError?.message,
      {
        position: "bottom-left",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
        transition: Bounce,
      }
    );
  }

  // if (!vtourOutputId1 || !output) {
  //   return <Typography>Couldn't find output</Typography>;
  // }

  // if (
  //   !output.processingStartTime ||
  //   !output.processingEndTime ||
  //   !output.s3Path
  // ) {
  //   return <Typography>Not ready dood!</Typography>;
  // }

  if (spanFullScreen) {
    return (
      <UnityViewerContextProvider>
        <Stack sx={{ height: "100vh", overflow: "hidden", width: "100vw" }}>
          <UnityViewer scanType={mode} />
          {output && output.virtualTour && !isPublic && (
            <DisplayAnnotation
              annotation={selectedAnnotation}
              onClose={() => {
                setSearchParams((prev) => {
                  const updatedParams = new URLSearchParams(prev.toString());
                  updatedParams.delete("annotationId");
                  return updatedParams;
                });
              }}
              tourId={output?.virtualTour?.id}
            />
          )}
        </Stack>
      </UnityViewerContextProvider>
    );
  }

  return (
    <UnityViewerContextProvider>
      <Stack
        sx={{
          height: "100%",
          position: "relative",
        }}
      >
        <Stack flex={1} alignItems="center" justifyContent="center">
          <Stack
            width={"100%"}
            height="100%"
            gap={"1rem"}
            maxHeight={fullscreen ? "100vh" : "68vh"}
          >
            <Stack
              // ref={ref}
              direction="row"
              width="100%"
              height="100%"
              position="relative"
              borderRadius={5}
            >
              {output && output.virtualTour && newAnnotation && (
                <NewAnnotationDialog
                  tourId={output.virtualTour.id}
                  onClose={() => {
                    console.debug("Closing new annotation dialog");
                    setNewAnnotation(null);
                  }}
                />
              )}
              {fullscreen && <Toolbar isOverlap={true} />}
              <UnityViewer scanType={mode} />
              {output && output.virtualTour && !isPublic && (
                <DisplayAnnotation
                  annotation={selectedAnnotation}
                  onClose={() => {
                    setSearchParams((prev) => {
                      const updatedParams = new URLSearchParams(prev.toString());
                      updatedParams.delete("annotationId");
                      return updatedParams;
                    });
                  }}
                  tourId={output.virtualTour.id}
                />
              )}
            </Stack>
            {/* <Stack
              direction="row"
              justifyContent="space-between"
              style={{
                color: "white",
              }}
            >
              <div
                id="unity-webgl-logo"
                style={{
                  filter: "invert(.5)",
                  height: "1.75rem",
                  backgroundSize: "contain",
                  backgroundPosition: "left center",
                }}
              ></div>
              <Stack direction="row" spacing={2} alignItems="center">
                {/* <div
                id="unity-build-title"
                style={{
                  color: "#aaad",
                  fontSize: "1.4rem",
                  fontWeight: "bold",
                  // letterSpacing: 1,
                }}
              >
                Virtual Tour
              </div> */}
            {/* <div
                id="unity-fullscreen-button"
                onClick={() => ref.current?.requestFullscreen()}
                style={{
                  backgroundColor: "#000",
                }}
              /> */}
            {/* <Stack
                  direction={"row"}
                  alignItems={"center"}
                  spacing={1}
                  onClick={() => ref.current?.requestFullscreen()}
                  sx={{ cursor: "pointer" }}
                >
                  <div
                    // id="unity-build-title"
                    style={{
                      color: "#aaad",
                      fontSize: "1rem",
                      fontWeight: "bold",
                      // letterSpacing: 1,
                    }}
                  >
                    Enter Full Screen
                  </div>
                  <FullScreenIcon size={24} />
                </Stack>
              </Stack> */}
            {/* </Stack> */}
          </Stack>
        </Stack>
        {/* modal here */}
        {output?.virtualTour && !output?.virtualTour.isScaled && (
          <ExistingReferenceMeasurements
            isPublic={isPublic}
            referenceMeasurements={referenceMeasurements}
            virtualTourID={output?.virtualTour?.id || ""}
          />
        )}
      </Stack>
    </UnityViewerContextProvider>
  );
};

export default VirtualTourViewer;
